<script setup lang="ts">
import { computed, inject } from 'vue'

import { InputComponent, TypographyComponent } from '../../../'
import { ANALYTICS_WINDOW_MAX_DAYS, ANALYTICS_WINDOW_MIN_DAYS } from '../../../../adapters'
import { ANALYTICS_METRIC_OPTIONS, formatterProviderKey } from '../../../../constants'
import { defaultFormatter } from '../../../../formatters'
import type { AdvertiserGroup, AnalyticsCriterion, LoadAdvertisersForCriteria } from '../../../../types'
import CriteriaAsyncEntitySelect from '../CriteriaAsyncEntitySelect.vue'
import CriteriaEnumSelect from '../CriteriaEnumSelect.vue'
import CriteriaFieldCell from '../CriteriaFieldCell.vue'
import CriteriaOperatorValueField from '../CriteriaOperatorValueField.vue'
import { useNumericField } from '../numeric-field.helper'
import { useCriterionField } from '../useCriterionField'

const props = withDefaults(
  defineProps<{
    row: AnalyticsCriterion
    loadAdvertisers?: LoadAdvertisersForCriteria
    hasAdvertiserSelect?: boolean
    advertiserGroup?: Pick<AdvertiserGroup, 'currency'> | null
    isDisabled?: boolean
  }>(),
  { loadAdvertisers: undefined, hasAdvertiserSelect: true, advertiserGroup: null, isDisabled: false },
)
const emits = defineEmits<(e: 'update:row', row: AnalyticsCriterion) => void>()
const { getCurrencyLabel } = inject(formatterProviderKey, defaultFormatter)

const advertiser = useCriterionField(props, emits, 'advertiser')
const metric = useCriterionField(props, emits, 'metric')
const operator = useCriterionField(props, emits, 'operator')
const value = useCriterionField(props, emits, 'value')
const windowDays = useNumericField(useCriterionField(props, emits, 'windowDays'), {
  min: ANALYTICS_WINDOW_MIN_DAYS,
  max: ANALYTICS_WINDOW_MAX_DAYS,
})

const selectedMetric = computed(() => ANALYTICS_METRIC_OPTIONS.find((option) => option.value === props.row.metric) ?? null)
const valueSuffix = computed(() => {
  if (!selectedMetric.value) {
    return ''
  }
  if (selectedMetric.value.unit === '%') {
    return '%'
  }
  const currency = props.advertiserGroup?.currency ?? props.row.advertiser?.currency
  return currency ? getCurrencyLabel(currency) : ''
})
</script>

<template>
  <div class="analytics-fields">
    <CriteriaFieldCell v-if="hasAdvertiserSelect && loadAdvertisers">
      <CriteriaAsyncEntitySelect
        v-model="advertiser"
        :is-disabled="isDisabled"
        :load-options="loadAdvertisers"
        placeholder="All advertisers"
        size="large"
      />
    </CriteriaFieldCell>
    <CriteriaFieldCell>
      <CriteriaEnumSelect
        v-model="metric"
        :is-disabled="isDisabled"
        :options="ANALYTICS_METRIC_OPTIONS"
        placeholder="Metric"
        size="large"
      />
    </CriteriaFieldCell>
    <template v-if="row.metric">
      <CriteriaOperatorValueField v-model:operator="operator" v-model:value="value" :precision="2" :is-disabled="isDisabled" size="large">
        <template v-if="valueSuffix" #suffix>
          <TypographyComponent variant="title-large" :font-weight="600">{{ valueSuffix }}</TypographyComponent>
        </template>
      </CriteriaOperatorValueField>
      <CriteriaFieldCell>
        <InputComponent
          v-model.number="windowDays"
          :is-disabled="isDisabled"
          type="number"
          :min="ANALYTICS_WINDOW_MIN_DAYS"
          :max="ANALYTICS_WINDOW_MAX_DAYS"
          placeholder="1"
        >
          <template #suffix>
            <TypographyComponent variant="title-large" :font-weight="600">days ago</TypographyComponent>
          </template>
        </InputComponent>
      </CriteriaFieldCell>
    </template>
  </div>
</template>

<style scoped lang="scss">
.analytics-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}
</style>
